"use client";

import React, { createContext, useContext, useMemo } from "react";
import type { WikiKind } from "@/utilities/data/getTeamPosts";
import type { TeamPageProjection } from "@/utilities/data/team/projections/forTeamPage";
import type { AdaptedTeamEvent } from "@/utilities/data/team/types";
import type { Post } from "@/payload-types";

/**
 * The team-page registry augments the projection with Payload-fetched posts,
 * wiki posts, and the derived site-mode flag. This is the shape every
 * client partial reads via `useTeamPageData()`.
 */
export interface TeamPageContextData extends TeamPageProjection {
    posts: {
        articles: Post[];
        totalArticles: number;
        totalPages: number;
        articlesPerPage: number;
        videos: Post[];
        totalVideos: number;
        videosTotalPages: number;
        videosPerPage: number;
    };
    wikiPosts: Partial<Record<WikiKind, Post>>;
    isSingleCompetition: boolean;
}

/**
 * Extended context value with computed helpers.
 */
export interface TeamPageDataContextValue extends TeamPageContextData {
    getNextMatch: () => AdaptedTeamEvent | undefined;
    getPreviousMatch: () => AdaptedTeamEvent | undefined;
    getUpcomingMatches: () => AdaptedTeamEvent[];
    getFinishedMatches: () => AdaptedTeamEvent[];
    isMultiCompetition: boolean;
    getArticles: () => Post[];
    getTotalArticles: () => number;
    getTotalPages: () => number;
    getArticlesPerPage: () => number;
    getVideos: () => Post[];
    getTotalVideos: () => number;
    getVideosTotalPages: () => number;
    getVideosPerPage: () => number;
    hasAnyVideos: () => boolean;
    getWikiPost: (kind: WikiKind) => Post | undefined;
    hasAnyWikiPosts: () => boolean;
    getAvailableWikiKinds: () => WikiKind[];
}

function isTeamEvent(ev: AdaptedTeamEvent, teamId: string): boolean {
    return ev.competitorOne.id === teamId || ev.competitorTwo.id === teamId;
}

function isUpcoming(ev: AdaptedTeamEvent): boolean {
    const t = ev.status.type;
    return t !== "FINISHED" && t !== "LIVE";
}

function isFinished(ev: AdaptedTeamEvent): boolean {
    return ev.status.type === "FINISHED";
}

const byDateAsc = (a: AdaptedTeamEvent, b: AdaptedTeamEvent) =>
    new Date(a.date).getTime() - new Date(b.date).getTime();
const byDateDesc = (a: AdaptedTeamEvent, b: AdaptedTeamEvent) =>
    new Date(b.date).getTime() - new Date(a.date).getTime();

const TeamPageDataContext = createContext<TeamPageDataContextValue | null>(null);

interface TeamPageDataProviderProps {
    data: TeamPageContextData;
    children: React.ReactNode;
}

export function TeamPageDataProvider({ data, children }: TeamPageDataProviderProps) {
    const contextValue = useMemo<TeamPageDataContextValue>(() => {
        const teamId = data.team.id;
        const teamEvents = data.events.filter((e) => isTeamEvent(e, teamId));

        return {
            ...data,
            getNextMatch: () => teamEvents.filter(isUpcoming).sort(byDateAsc)[0],
            getPreviousMatch: () => teamEvents.filter(isFinished).sort(byDateDesc)[0],
            getUpcomingMatches: () => teamEvents.filter(isUpcoming).sort(byDateAsc),
            getFinishedMatches: () => teamEvents.filter(isFinished).sort(byDateDesc),
            isMultiCompetition: !data.isSingleCompetition,
            getArticles: () => data.posts.articles,
            getTotalArticles: () => data.posts.totalArticles,
            getTotalPages: () => data.posts.totalPages,
            getArticlesPerPage: () => data.posts.articlesPerPage,
            getVideos: () => data.posts.videos,
            getTotalVideos: () => data.posts.totalVideos,
            getVideosTotalPages: () => data.posts.videosTotalPages,
            getVideosPerPage: () => data.posts.videosPerPage,
            hasAnyVideos: () => data.posts.totalVideos > 0,
            getWikiPost: (kind: WikiKind) => data.wikiPosts[kind],
            hasAnyWikiPosts: () => Object.keys(data.wikiPosts).length > 0,
            getAvailableWikiKinds: () => Object.keys(data.wikiPosts) as WikiKind[],
        };
    }, [data]);

    return <TeamPageDataContext.Provider value={contextValue}>{children}</TeamPageDataContext.Provider>;
}

/**
 * Hook to access team page data in client components.
 * Must be used within a TeamPageDataProvider.
 */
export function useTeamPageData(): TeamPageDataContextValue {
    const context = useContext(TeamPageDataContext);
    if (!context) {
        throw new Error("useTeamPageData must be used within a TeamPageDataProvider");
    }
    return context;
}

/**
 * Hook to optionally access team page data.
 * Returns null if not within a TeamPageDataProvider (useful for shared components).
 */
export function useTeamPageDataOptional(): TeamPageDataContextValue | null {
    return useContext(TeamPageDataContext);
}
